/**
 * Directe toegang tot de Plex-server vanuit de browser (zoals Plex Web).
 * Het token gaat als query-parameter mee, zodat er geen CORS-preflight nodig is.
 */

// ---------- Types (alleen de velden die we gebruiken) ----------

export interface PlexStream {
  id: number;
  streamType: 1 | 2 | 3; // 1 video, 2 audio, 3 ondertitels
  /** Alleen bij losse (externe) ondertitelbestanden: pad om het bestand op te halen */
  key?: string;
  codec?: string;
  displayTitle?: string;
  extendedDisplayTitle?: string;
  language?: string;
  languageCode?: string;
  selected?: boolean;
  default?: boolean;
  forced?: boolean;
  DOVIPresent?: boolean;
  DOVIProfile?: number;
  colorTrc?: string;
  decision?: 'copy' | 'transcode' | 'burn' | string;
}

/** Zoekresultaat van ondertitels (OpenSubtitles via de Plex-server) */
export type SubtitleResult = PlexStream & { score?: string | number; providerTitle?: string; hearingImpaired?: boolean; title?: string };

export interface PlexPart {
  id: number;
  key: string;
  file?: string;
  size?: number;
  container?: string;
  duration?: number;
  Stream?: PlexStream[];
  decision?: string;
  /** 'sd' als Plex voorbeeldplaatjes (BIF) heeft gemaakt */
  indexes?: string;
}

export interface PlexMedia {
  id: number;
  duration?: number;
  bitrate?: number;
  width?: number;
  height?: number;
  container?: string;
  videoCodec?: string;
  audioCodec?: string;
  audioChannels?: number;
  videoResolution?: string;
  videoDynamicRange?: string;
  audioProfile?: string;
  Part: PlexPart[];
}

export interface PlexTag {
  id?: number;
  tag: string;
  role?: string;
  thumb?: string;
}

export interface PlexMarker {
  type: 'intro' | 'credits' | string;
  startTimeOffset: number;
  endTimeOffset: number;
  final?: boolean;
}

export interface PlexItem {
  ratingKey: string;
  key: string;
  type: 'movie' | 'show' | 'season' | 'episode' | string;
  title: string;
  titleSort?: string;
  originalTitle?: string;
  tagline?: string;
  librarySectionID?: number;
  grandparentTitle?: string;
  grandparentRatingKey?: string;
  grandparentGuid?: string;
  parentTitle?: string;
  parentRatingKey?: string;
  parentYear?: number;
  parentIndex?: number;
  index?: number;
  year?: number;
  originallyAvailableAt?: string;
  summary?: string;
  contentRating?: string;
  rating?: number;
  audienceRating?: number;
  duration?: number;
  viewOffset?: number;
  viewCount?: number;
  /** Laatst bekeken (seconden sinds 1970) */
  lastViewedAt?: number;
  leafCount?: number;
  viewedLeafCount?: number;
  childCount?: number;
  thumb?: string;
  art?: string;
  parentThumb?: string;
  grandparentThumb?: string;
  grandparentArt?: string;
  Media?: PlexMedia[];
  Genre?: PlexTag[];
  Director?: PlexTag[];
  Role?: PlexTag[];
  Marker?: PlexMarker[];
  /** Plex-id, bijv. 'plex://movie/5d776825...' (ook voor de watchlist) */
  guid?: string;
  /** Met includeGuids=1: bijv. { id: 'tmdb://603' }, { id: 'imdb://tt0133093' } */
  Guid?: { id: string }[];
  Image?: { type: string; url: string }[];
  Extras?: { Metadata?: PlexItem[] };
  OnDeck?: { Metadata?: PlexItem[] };
}

export interface PlexLibrary {
  key: string;
  title: string;
  type: 'movie' | 'show' | 'artist' | 'photo' | string;
}

export interface PlexHub {
  hubIdentifier: string;
  title: string;
  type: string;
  Metadata?: PlexItem[];
}

export interface LibraryQuery {
  sort?: string;
  genre?: string;
  unwatched?: boolean;
  resolution?: '4k' | '1080';
  hdr?: boolean;
  /** Plex-type: 1 film, 2 serie, 4 aflevering */
  type?: number;
  start: number;
  size: number;
}

interface Container<T> {
  MediaContainer: T & { size?: number; totalSize?: number };
}

// ---------- Client ----------

const CLIENT_KEY = 'tumedia.clientId';

/**
 * Plex kiest het afspeelprofiel (welke codecs de client aankan) op basis van
 * product + platform. Met een onbekend product valt Plex terug op een generiek
 * profiel dat ook HEVC/TrueHD doorstuurt, wat een browser niet kan afspelen.
 * Daarom melden we ons voor afspelen als "Plex Web"; de apparaatnaam blijft "tumedia".
 */
const PLEX_PRODUCT = 'Plex Web';
/** Versie van Plex Web waarvan we het afspeelgedrag nadoen (Plex kiest profielregels mede op versie). */
const PLEX_WEB_VERSION = '4.162.1';

/**
 * Willekeurige ID. crypto.randomUUID bestaat alleen in een "secure context" (https of localhost);
 * via http://<lan-ip> is het er niet. getRandomValues werkt wel overal.
 */
export function uuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** Stabiele ID per browser, zodat Plex sessies en voortgang aan dit apparaat koppelt. */
export function clientIdentifier(): string {
  try {
    let id = localStorage.getItem(CLIENT_KEY);
    if (!id) {
      id = `tumedia-web-${uuid()}`;
      localStorage.setItem(CLIENT_KEY, id);
    }
    return id;
  } catch {
    return 'tumedia-web';
  }
}

export function browserPlatform(): 'Safari' | 'Chrome' | 'Firefox' {
  const ua = navigator.userAgent;
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua) && !/Chrome\/|Chromium\/|Edg\//.test(ua)) return 'Safari';
  return 'Chrome';
}

/** Keuring zoals Plex die geeft ("nl/16", "16", "R", "TV-MA", "AL") naar een leeftijd; onbekend = null. */
export function plexRatingAge(r: string | undefined): number | null {
  if (!r) return null;
  const v = r.split('/').pop()!.trim().toUpperCase();
  if (v === 'AL') return 0;
  if (/^\d{1,2}$/.test(v)) return Number(v);
  const map: Record<string, number> = { G: 0, PG: 9, 'PG-13': 13, R: 17, 'NC-17': 18, 'TV-Y': 0, 'TV-Y7': 7, 'TV-G': 0, 'TV-PG': 9, 'TV-14': 14, 'TV-MA': 17 };
  return map[v] ?? null;
}

/**
 * Mag dit profiel deze titel zien? Films en series zonder keuring vallen weg (zoals bij een kinderprofiel
 * van een streamingdienst); afleveringen en seizoenen alleen als hun eigen keuring te hoog is
 * (de serie zelf is dan al gecontroleerd). Muziek, collecties en de rest blijven staan.
 */
export function allowedForAge(item: { type?: string; contentRating?: string; grandparentContentRating?: string }, maxAge: number | null): boolean {
  if (maxAge === null) return true;
  if (item.type === 'movie' || item.type === 'show') {
    const a = plexRatingAge(item.contentRating);
    return a !== null && a <= maxAge;
  }
  if (item.type === 'episode' || item.type === 'season') {
    const a = plexRatingAge(item.contentRating ?? item.grandparentContentRating);
    return a === null || a <= maxAge;
  }
  return true;
}

/**
 * Trage of onbereikbare Plex-server: 2 minuten lang wachten Home-rijen en hubs er niet op (ze blijven leeg of
 * tonen alleen bronnen van buiten Plex). Afspelen en de titelpagina proberen het gewoon.
 */
const SKIPPABLE = /^\/(hubs|library\/(onDeck|recentlyAdded|sections\/[^/]+\/(recentlyAdded|onDeck|newest)))\b/;
export const plexSlow = { until: 0, lastProbe: 0 };
function markPlexSlow(): void {
  const was = plexSlow.until > Date.now();
  plexSlow.until = Date.now() + 120_000;
  plexSlow.lastProbe = Date.now();
  if (!was) window.dispatchEvent(new CustomEvent('tumedia:plex-slow', { detail: true }));
}

export class PlexClient {
  readonly clientId = clientIdentifier();
  /** Leeftijdsgrens van het profiel (null = geen); verzoeken wachten tot die bekend is */
  private maxAge: number | null = null;
  private ageReady: Promise<void>;
  private resolveAge!: () => void;

  /** Account of profiel zonder Plex: alle lijsten zijn leeg en er gaat niets naar een server. */
  offline = false;

  static offline(): PlexClient {
    const c = new PlexClient('http://plex.invalid', '');
    c.offline = true;
    c.setMaxAge(null);
    return c;
  }

  constructor(readonly baseUrl: string, private token: string) {
    this.ageReady = new Promise((r) => (this.resolveAge = r));
    // Nooit eeuwig wachten als het beleid niet geladen kan worden
    window.setTimeout(() => this.resolveAge(), 8000);
  }

  /** Leeftijdsgrens van het actieve profiel; titels daarboven komen nergens meer terug (bibliotheek, zoeken, rijen). */
  setMaxAge(maxAge: number | null): void {
    this.maxAge = maxAge;
    this.resolveAge();
  }

  get ageLimit(): number | null {
    return this.maxAge;
  }

  /** Lijsten (Metadata en hubs) filteren op de leeftijdsgrens. */
  private ageFilter<T>(data: T): T {
    if (this.maxAge === null) return data;
    const mc = (data as { MediaContainer?: { Metadata?: PlexItem[]; Hub?: { Metadata?: PlexItem[] }[] } }).MediaContainer;
    if (!mc) return data;
    const ok = (m: PlexItem) => allowedForAge(m as PlexItem & { grandparentContentRating?: string }, this.maxAge);
    if (mc.Metadata) mc.Metadata = mc.Metadata.filter(ok);
    if (mc.Hub) for (const h of mc.Hub) if (h.Metadata) h.Metadata = h.Metadata.filter(ok);
    return data;
  }

  /** Volledige URL met token en client-headers als query-parameters. */
  url(path: string, params: Record<string, string | number | boolean | undefined> = {}, withClientInfo = false): string {
    const u = new URL(path, this.baseUrl);
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) u.searchParams.set(k, String(v));
    }
    if (withClientInfo) {
      u.searchParams.set('X-Plex-Product', PLEX_PRODUCT);
      u.searchParams.set('X-Plex-Version', PLEX_WEB_VERSION);
      u.searchParams.set('X-Plex-Client-Identifier', this.clientId);
      u.searchParams.set('X-Plex-Platform', browserPlatform());
      u.searchParams.set('X-Plex-Device', browserPlatform());
      u.searchParams.set('X-Plex-Device-Name', 'tumedia Web');
    }
    u.searchParams.set('X-Plex-Token', this.token);
    return u.toString();
  }

  private async request<T>(
    path: string,
    params?: Record<string, string | number | boolean | undefined>,
    method: 'GET' | 'PUT' | 'POST' = 'GET',
    clientInfo = method !== 'GET',
    keepalive = false,
  ): Promise<T> {
    if (this.offline) {
      if (method !== 'GET') throw new Error('Geen Plex voor dit account');
      return { MediaContainer: { size: 0, totalSize: 0 } } as T;
    }
    // Trage Plex: lijsten (Home-rijen, hubs) niet laten wachten. Elke 30 s mag er één door om te kijken of hij er weer is.
    const optional = method === 'GET' && SKIPPABLE.test(path);
    if (optional && plexSlow.until > Date.now()) {
      if (Date.now() - plexSlow.lastProbe < 30_000) throw new Error('Plex reageert traag; even overgeslagen');
      plexSlow.lastProbe = Date.now();
    }
    let res: Response;
    try {
      res = await fetch(this.url(path, params, clientInfo), {
        method,
        headers: { Accept: 'application/json' },
        // keepalive: komt ook nog aan als de pagina net wordt gesloten of verlaten
        keepalive,
        signal: method === 'GET' ? AbortSignal.timeout(optional ? 8000 : 20_000) : undefined,
      });
    } catch (err) {
      if (method === 'GET') markPlexSlow();
      throw err;
    }
    if (method === 'GET' && plexSlow.until) {
      plexSlow.until = 0;
      window.dispatchEvent(new CustomEvent('tumedia:plex-slow', { detail: false }));
    }
    if (!res.ok) throw new Error(`Plex ${res.status} op ${path}`);
    const text = await res.text();
    const data = (text ? JSON.parse(text) : {}) as T;
    // Alleen lijsten uit de bibliotheek, hubs en playlists (niet afspeel-beslissingen e.d.)
    if (method !== 'GET' || !/^\/(library|hubs|playlists)\b/.test(path)) return data;
    await this.ageReady;
    return this.ageFilter(data);
  }

  /** Vrije GET op de Plex-server (muziek en andere onderdelen met eigen helpers). */
  get<T>(path: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
    return this.request<T>(path, params);
  }

  /** Titel in de bibliotheek op een externe id (bijv. "imdb://tt0133093"). */
  async findByGuid(guid: string, type: 1 | 2): Promise<PlexItem | null> {
    const libs = (await this.libraries()).filter((l) => l.type === (type === 1 ? 'movie' : 'show'));
    for (const l of libs) {
      const d = await this.request<Container<{ Metadata?: PlexItem[] }>>(`/library/sections/${l.key}/all`, { guid, type, includeGuids: 1 }).catch(() => null);
      const hit = d?.MediaContainer.Metadata?.find((m) => m.Guid?.some((g) => g.id === guid));
      if (hit) return hit;
    }
    return null;
  }

  // ----- Bibliotheek -----

  async libraries(): Promise<PlexLibrary[]> {
    const d = await this.request<Container<{ Directory?: PlexLibrary[] }>>('/library/sections');
    return d.MediaContainer.Directory ?? [];
  }

  async onDeck(): Promise<PlexItem[]> {
    const d = await this.request<Container<{ Metadata?: PlexItem[] }>>('/library/onDeck', {
      includeGuids: 1,
      'X-Plex-Container-Start': 0,
      'X-Plex-Container-Size': 20,
    });
    return d.MediaContainer.Metadata ?? [];
  }

  async recentlyAdded(sectionKey?: string): Promise<PlexItem[]> {
    const path = sectionKey ? `/library/sections/${sectionKey}/recentlyAdded` : '/library/recentlyAdded';
    const d = await this.request<Container<{ Metadata?: PlexItem[] }>>(path, {
      includeGuids: 1,
      'X-Plex-Container-Start': 0,
      'X-Plex-Container-Size': 30,
    });
    return d.MediaContainer.Metadata ?? [];
  }

  async libraryItems(sectionKey: string, q: LibraryQuery): Promise<{ items: PlexItem[]; total: number }> {
    const d = await this.request<Container<{ Metadata?: PlexItem[] }>>(`/library/sections/${sectionKey}/all`, {
      sort: q.sort ?? 'titleSort',
      includeGuids: 1,
      genre: q.genre,
      unwatched: q.unwatched ? 1 : undefined,
      resolution: q.resolution,
      hdr: q.hdr ? 1 : undefined,
      type: q.type,
      includeCollections: 0,
      'X-Plex-Container-Start': q.start,
      'X-Plex-Container-Size': q.size,
    });
    return { items: d.MediaContainer.Metadata ?? [], total: d.MediaContainer.totalSize ?? d.MediaContainer.size ?? 0 };
  }

  /** Voorbeeldplaatjes voor de tijdbalk (alleen als Plex ze heeft gemaakt) */
  bifUrl(part: PlexPart | undefined): string | null {
    return part?.indexes ? this.url(`/library/parts/${part.id}/indexes/${part.indexes}`, { interval: 10000 }) : null;
  }

  /** Ondertitels zoeken via de ondertitelbron van de Plex-server (OpenSubtitles). Taal als 2 letters ("nl"). */
  async searchSubtitles(ratingKey: string, lang2: string): Promise<SubtitleResult[]> {
    const d = await this.request<Container<{ Stream?: SubtitleResult[] }>>(
      `/library/metadata/${ratingKey}/subtitles`, { language: lang2, hearingImpaired: 0, forced: 0 },
    );
    return d.MediaContainer.Stream ?? [];
  }

  /** Gekozen ondertitel laten downloaden; Plex voegt hem daarna toe als spoor (duurt een paar seconden). */
  downloadSubtitle(ratingKey: string, key: string): Promise<unknown> {
    return this.request(`/library/metadata/${ratingKey}/subtitles`, { key }, 'PUT');
  }

  /** Collecties in een bibliotheek */
  async collections(sectionKey: string): Promise<{ ratingKey: string; title: string; childCount?: number }[]> {
    const d = await this.request<Container<{ Metadata?: { ratingKey: string; title: string; childCount?: number }[] }>>(`/library/sections/${sectionKey}/collections`);
    return d.MediaContainer.Metadata ?? [];
  }

  async collectionItems(ratingKey: string, size = 40, start = 0): Promise<PlexItem[]> {
    const d = await this.request<Container<{ Metadata?: PlexItem[] }>>(`/library/collections/${ratingKey}/children`, {
      includeGuids: 1, 'X-Plex-Container-Start': start, 'X-Plex-Container-Size': size,
    });
    return d.MediaContainer.Metadata ?? [];
  }

  /** Video-playlists van dit profiel */
  async playlists(): Promise<{ ratingKey: string; title: string; leafCount?: number }[]> {
    const d = await this.request<Container<{ Metadata?: { ratingKey: string; title: string; leafCount?: number }[] }>>('/playlists', { playlistType: 'video' });
    return d.MediaContainer.Metadata ?? [];
  }

  async playlistItems(ratingKey: string, size = 40, start = 0): Promise<PlexItem[]> {
    const d = await this.request<Container<{ Metadata?: PlexItem[] }>>(`/playlists/${ratingKey}/items`, {
      includeGuids: 1, 'X-Plex-Container-Start': start, 'X-Plex-Container-Size': size,
    });
    return d.MediaContainer.Metadata ?? [];
  }

  async genres(sectionKey: string): Promise<{ key: string; title: string }[]> {
    const d = await this.request<Container<{ Directory?: { key: string; title: string }[] }>>(
      `/library/sections/${sectionKey}/genre`,
    );
    return d.MediaContainer.Directory ?? [];
  }

  async metadata(ratingKey: string): Promise<PlexItem> {
    const d = await this.request<Container<{ Metadata?: PlexItem[] }>>(`/library/metadata/${ratingKey}`, {
      includeExtras: 1,
      includeMarkers: 1,
      includeGuids: 1,
      includeOnDeck: 1,
      includeChildren: 0,
      checkFiles: 0,
    });
    const item = d.MediaContainer.Metadata?.[0];
    if (!item) throw new Error(this.maxAge !== null ? 'Niet beschikbaar voor dit profiel.' : 'Item niet gevonden');
    return item;
  }

  /** Meerdere titels in één verzoek (in dezelfde volgorde als de sleutels). */
  async metadataMany(ratingKeys: string[]): Promise<PlexItem[]> {
    if (!ratingKeys.length) return [];
    const d = await this.request<Container<{ Metadata?: PlexItem[] }>>(`/library/metadata/${ratingKeys.map(encodeURIComponent).join(',')}`, { includeGuids: 1 });
    const byKey = new Map((d.MediaContainer.Metadata ?? []).map((m) => [m.ratingKey, m]));
    return ratingKeys.map((k) => byKey.get(k)).filter((m): m is PlexItem => !!m);
  }

  async children(ratingKey: string): Promise<PlexItem[]> {
    const d = await this.request<Container<{ Metadata?: PlexItem[] }>>(`/library/metadata/${ratingKey}/children`, { includeGuids: 1 });
    return d.MediaContainer.Metadata ?? [];
  }

  async search(query: string): Promise<PlexHub[]> {
    const d = await this.request<Container<{ Hub?: PlexHub[] }>>('/hubs/search', {
      query,
      limit: 20,
      includeGuids: 1,
      includeCollections: 0,
    });
    return (d.MediaContainer.Hub ?? []).filter((h) => (h.Metadata?.length ?? 0) > 0);
  }

  // ----- Kijkstatus -----

  /** Uit Verder kijken halen (zoals in Plex); kijkstand en bekeken-status blijven bewaard. */
  removeFromContinueWatching(ratingKey: string): Promise<unknown> {
    return this.request('/actions/removeFromContinueWatching', { ratingKey }, 'PUT');
  }

  markWatched(ratingKey: string): Promise<unknown> {
    return this.request('/:/scrobble', { identifier: 'com.plexapp.plugins.library', key: ratingKey });
  }

  markUnwatched(ratingKey: string): Promise<unknown> {
    return this.request('/:/unscrobble', { identifier: 'com.plexapp.plugins.library', key: ratingKey });
  }

  /** Voortgang doorgeven, zodat Verder kijken en Plex-dashboard kloppen. */
  timeline(item: PlexItem, state: 'playing' | 'paused' | 'stopped', timeMs: number, sessionId: string, final = false): Promise<unknown> {
    return this.request('/:/timeline', {
      ratingKey: item.ratingKey,
      key: `/library/metadata/${item.ratingKey}`,
      state,
      time: Math.round(timeMs),
      duration: item.duration,
      'X-Plex-Session-Identifier': sessionId,
    }, 'GET', true, final);
  }

  /** Audio- of ondertitelspoor kiezen; Plex onthoudt de keuze per bestand. */
  selectStreams(partId: number, opts: { audioStreamID?: number; subtitleStreamID?: number }): Promise<unknown> {
    return this.request(`/library/parts/${partId}`, { ...opts, allParts: 1 }, 'PUT');
  }

  // ----- Afspelen -----

  /** Laat Plex beslissen: direct afspelen, remuxen of transcoderen voor deze browser. */
  async decision(opts: PlaybackOptions): Promise<PlaybackDecision> {
    const d = await this.request<Container<{
      generalDecisionCode?: number;
      generalDecisionText?: string;
      transcodeDecisionText?: string;
      Metadata?: PlexItem[];
    }>>('/video/:/transcode/universal/decision', transcodeParams(opts));
    const part = d.MediaContainer.Metadata?.[0]?.Media?.[0]?.Part?.[0];
    const video = part?.Stream?.find((s) => s.streamType === 1);
    const audio = part?.Stream?.find((s) => s.streamType === 2);
    return {
      code: d.MediaContainer.generalDecisionCode,
      videoCodec: video?.codec,
      videoDecision: video?.decision ?? 'transcode',
      audioDecision: audio?.decision ?? 'transcode',
      reason: d.MediaContainer.transcodeDecisionText ?? d.MediaContainer.generalDecisionText,
    };
  }

  hlsUrl(opts: PlaybackOptions): string {
    return this.url('/video/:/transcode/universal/start.m3u8', transcodeParams(opts), true);
  }

  /** DASH-stream (fMP4), zoals Plex Web die gebruikt voor HEVC zonder transcode. */
  dashUrl(opts: PlaybackOptions): string {
    return this.url('/video/:/transcode/universal/start.mpd', transcodeParams({ ...opts, protocol: 'dash' }), true);
  }

  stopTranscode(sessionId: string): void {
    void fetch(this.url('/video/:/transcode/universal/stop', { session: sessionId }, true), { keepalive: true }).catch(() => undefined);
  }

  directUrl(part: PlexPart): string {
    return this.url(part.key);
  }

  /** Afbeelding via de Plex-transcoder op de juiste maat (scheelt veel bandbreedte op tv/mobiel). */
  image(path: string | undefined, width: number, height: number): string | undefined {
    if (!path) return undefined;
    // Externe afbeeldingen (bijv. cast-foto's van metadata.provider.plex.tv) gaan ook via de transcoder
    return this.url('/photo/:/transcode', { url: path, width, height, minSize: 1, upscale: 1 });
  }
}

export interface PlaybackOptions {
  ratingKey: string;
  mediaIndex: number;
  sessionId: string;
  clientId: string;
  /** undefined = origineel (Plex mag direct streamen) */
  maxBitrateKbps?: number;
  offsetSec?: number;
  /** 'burn' = inbranden (kost een video-transcode), 'none' = wij tonen ze zelf of ze staan uit */
  subtitles?: 'burn' | 'none';
  /** 'dash' = fMP4-segmenten (nodig voor HEVC in de browser); standaard 'hls' (MPEG-TS) */
  protocol?: 'hls' | 'dash';
  /** Plex laten weten dat deze browser HEVC kan afspelen, zodat de video niet getranscodeerd wordt */
  hevc?: boolean;
}

/**
 * Kan deze browser HEVC Main 10 (4K HDR) decoderen via Media Source Extensions?
 * Chrome en Edge kunnen dat met hardware-ondersteuning (Windows/macOS), Safari altijd.
 */
export function browserSupportsHevc(): boolean {
  try {
    const ms = (window as unknown as { MediaSource?: { isTypeSupported(t: string): boolean } }).MediaSource;
    if (!ms) return false;
    return ms.isTypeSupported('video/mp4; codecs="hvc1.2.4.L153.B0"') || ms.isTypeSupported('video/mp4; codecs="hev1.2.4.L153.B0"');
  } catch {
    return false;
  }
}

/**
 * Aanvulling op het "Plex Web"-profiel, letterlijk zoals Plex Web 4.162 die meestuurt als Chrome HEVC aankan:
 * - replace=true overschrijft de standaardgrenzen voor HEVC in het profiel (o.a. 8-bit);
 * - de colorTrc-regel staat HDR (PQ/bt2020) toe, anders transcodeert Plex HDR-video altijd;
 * - HEVC wordt een geldig doel voor DASH, naast H.264.
 * Let op: "h264%2Chevc" is bewust al gecodeerd; Plex verwacht de komma zo binnen de regel.
 */
const HEVC_PROFILE_EXTRA = [
  'add-limitation(scope=videoCodec&scopeName=hevc&type=upperBound&name=video.width&value=4096&replace=true)',
  'add-limitation(scope=videoCodec&scopeName=hevc&type=upperBound&name=video.height&value=2160&replace=true)',
  'add-limitation(scope=videoCodec&scopeName=hevc&type=upperBound&name=video.bitDepth&value=10&replace=true)',
  'append-transcode-target-codec(type=videoProfile&context=streaming&protocol=dash&videoCodec=hevc)',
  'add-limitation(scope=videoTranscodeTarget&scopeName=hevc&scopeType=videoCodec&context=streaming&protocol=dash&type=match&name=video.colorTrc&list=bt709|bt470m|bt470bg|smpte170m|smpte240m|bt2020-10|smpte2084&isRequired=false)',
  'append-transcode-target-codec(type=videoProfile&context=streaming&videoCodec=h264%2Chevc&audioCodec=aac&protocol=dash)',
].join('+');

export interface PlaybackDecision {
  /** 1000/1001 = kan afspelen; 2000+ = Plex kan dit niet voor deze client */
  code?: number;
  videoDecision: string;
  videoCodec?: string;
  audioDecision: string;
  reason?: string;
}

function transcodeParams(o: PlaybackOptions): Record<string, string | number> {
  const original = o.maxBitrateKbps === undefined;
  const p: Record<string, string | number> = {
    path: `/library/metadata/${o.ratingKey}`,
    mediaIndex: o.mediaIndex,
    partIndex: 0,
    protocol: o.protocol ?? 'hls',
    fastSeek: 1,
    directPlay: 0, // directe bestanden spelen we zelf af (zie Player)
    directStream: original ? 1 : 0,
    directStreamAudio: 1,
    subtitleSize: 100,
    audioBoost: 100,
    location: 'lan',
    autoAdjustQuality: 0,
    mediaBufferSize: 102400,
    session: o.sessionId,
    'X-Plex-Session-Identifier': o.sessionId,
    'X-Plex-Client-Identifier': o.clientId,
    'X-Plex-Platform': browserPlatform(),
    'X-Plex-Product': PLEX_PRODUCT,
    'X-Plex-Version': PLEX_WEB_VERSION,
    'X-Plex-Device-Name': 'tumedia',
    subtitles: o.subtitles ?? 'burn',
  };
  if (!original) {
    p.maxVideoBitrate = o.maxBitrateKbps!;
    p.videoBitrate = o.maxBitrateKbps!;
    p.videoQuality = 100;
    p.videoResolution = o.maxBitrateKbps! >= 8000 ? '1920x1080' : o.maxBitrateKbps! >= 4000 ? '1280x720' : '854x480';
  }
  if (o.offsetSec) p.offset = Math.floor(o.offsetSec);
  if (o.hevc && original) p['X-Plex-Client-Profile-Extra'] = HEVC_PROFILE_EXTRA;
  return p;
}

// ---------- Ondertitels ----------

const TEXT_SUB_CODECS = new Set(['srt', 'subrip', 'vtt', 'webvtt']);

/** Tekstondertitels die de browser zelf kan tonen (los bestand naast de video). */
export function isSidecarText(s: PlexStream | undefined): boolean {
  return !!s && !!s.key && TEXT_SUB_CODECS.has((s.codec ?? '').toLowerCase());
}

/** SRT omzetten naar WebVTT (het enige formaat dat <track> kent). */
export function srtToVtt(srt: string): string {
  const body = srt
    .replace(/^\uFEFF/, '')
    .replace(/\r\n?/g, '\n')
    .replace(/(\d{2}:\d{2}:\d{2}),(\d{3})/g, '$1.$2');
  return body.trimStart().startsWith('WEBVTT') ? body : `WEBVTT\n\n${body}`;
}

export async function loadSubtitleTrack(plex: PlexClient, s: PlexStream): Promise<string> {
  const res = await fetch(plex.url(s.key!));
  if (!res.ok) throw new Error(`Ondertitels ${res.status}`);
  const vtt = srtToVtt(await res.text());
  return URL.createObjectURL(new Blob([vtt], { type: 'text/vtt' }));
}

// ---------- Helpers voor weergave ----------

/** Korte badges voor de UI, op basis van een mediaversie. */
export function badges(item: PlexItem, media: PlexMedia | undefined = item.Media?.[0]): string[] {
  if (!media) return [];
  const out: string[] = [];
  const video = media.Part?.[0]?.Stream?.find((s) => s.streamType === 1);
  if (media.videoResolution === '4k') out.push('4K');
  else if (media.videoResolution === '1080') out.push('1080p');
  if (video?.DOVIPresent) out.push(video.DOVIProfile ? `Dolby Vision P${video.DOVIProfile}` : 'Dolby Vision');
  if (media.videoDynamicRange?.includes('HDR10+')) out.push('HDR10+');
  else if (media.videoDynamicRange && media.videoDynamicRange !== 'SDR' && !video?.DOVIPresent) out.push(media.videoDynamicRange);
  if (media.audioProfile === 'ma') out.push('DTS-HD MA');
  const audioTitle = media.Part?.[0]?.Stream?.find((s) => s.streamType === 2)?.extendedDisplayTitle ?? '';
  if (/atmos/i.test(audioTitle)) out.push('Atmos');
  else if (media.audioCodec === 'truehd') out.push('TrueHD');
  return out;
}

/** Alle labels voor de Badge Studio: zoals badges(), plus codec, remux en kanalen. */
export function mediaBadgeLabels(item: PlexItem, media: PlexMedia | undefined = item.Media?.[0]): string[] {
  const out = badges(item, media);
  const codec = media?.videoCodec?.toLowerCase();
  if (codec === 'hevc') out.push('HEVC');
  else if (codec === 'av1') out.push('AV1');
  const file = media?.Part?.[0]?.file ?? '';
  if (/\bremux\b/i.test(file)) out.push('Remux');
  if (/\bdts[ .-]?x\b/i.test(file)) out.push('DTS:X');
  const ch = media?.audioChannels;
  if (ch) out.push(ch >= 8 ? '7.1' : ch >= 6 ? '5.1' : '');
  return out.filter(Boolean);
}

/**
 * Vlaggen voor tegels (4K, DV, HDR, Atmos…). Lijsten van Plex hebben geen sporen,
 * dus naast de media-info ook de bestandsnaam ("…DV.HDR10…TrueHD.Atmos…").
 */
export function tileFlags(item: PlexItem): { label: string; kind: 'res' | 'hdr' | 'audio' }[] {
  const media = item.Media?.[0];
  if (!media) return [];
  const out: { label: string; kind: 'res' | 'hdr' | 'audio' }[] = [];
  const file = (media.Part?.[0]?.file ?? '').split(/[\\/]/).slice(-2).join(' ');
  if (media.videoResolution === '4k' || /\b(2160p|4k|uhd)\b/i.test(file)) out.push({ label: '4K', kind: 'res' });
  else if (media.videoResolution === '1080') out.push({ label: '1080p', kind: 'res' });
  const dr = media.videoDynamicRange ?? '';
  const dv = /dolby vision|dovi/i.test(dr) || /\b(dv|dovi|dolby[ .]?vision)\b/i.test(file);
  const hdr10p = /hdr10\+/i.test(dr) || /\bhdr10(\+|plus|p)\b/i.test(file);
  const hdr = (!!dr && dr !== 'SDR') || /\bhdr(10)?\b/i.test(file);
  if (dv) out.push({ label: 'DV', kind: 'hdr' });
  if (hdr10p) out.push({ label: 'HDR10+', kind: 'hdr' });
  else if (hdr && !dv) out.push({ label: 'HDR', kind: 'hdr' });
  if (/atmos/i.test(file) || /atmos/i.test(media.audioProfile ?? '')) out.push({ label: 'Atmos', kind: 'audio' });
  else if (/\bdts[ .-]?x\b/i.test(file)) out.push({ label: 'DTS:X', kind: 'audio' });
  else if (media.audioCodec === 'truehd') out.push({ label: 'TrueHD', kind: 'audio' });
  else if (media.audioProfile === 'ma' || /dts[ .-]?hd[ .-]?ma/i.test(file)) out.push({ label: 'DTS-HD', kind: 'audio' });
  return out;
}

export function versionLabel(media: PlexMedia): string {
  const res = media.videoResolution === '4k' ? '2160p' : media.videoResolution ? `${media.videoResolution}p` : '';
  const size = media.Part?.[0]?.size;
  const gb = size ? ` · ${(size / 1024 ** 3).toFixed(1)} GB` : '';
  const file = media.Part?.[0]?.file?.split(/[\\/]/).pop();
  const remux = file && /remux/i.test(file) ? ' Remux' : '';
  return `${res}${remux}${gb}`.trim() || `Versie ${media.id}`;
}

/** Hoofdtitel voor weergave: bij seizoenen en afleveringen de naam van de serie. */
export function displayTitle(item: PlexItem): string {
  if (item.type === 'episode') return item.grandparentTitle ?? item.title;
  if (item.type === 'season') return item.parentTitle ?? item.title;
  return item.title;
}

export function subtitle(item: PlexItem): string {
  if (item.type === 'season') return [item.title, item.parentYear ?? item.year].filter(Boolean).join(' · ');
  if (item.type === 'episode') {
    return `${item.grandparentTitle ?? ''} · S${item.parentIndex ?? '?'} E${item.index ?? '?'}`;
  }
  if (item.type === 'show' && item.childCount) return `${item.year ?? ''} · ${item.childCount} seizoen${item.childCount === 1 ? '' : 'en'}`;
  return item.year ? String(item.year) : '';
}

export function progress(item: PlexItem): number | undefined {
  if (!item.viewOffset || !item.duration) return undefined;
  return Math.min(1, item.viewOffset / item.duration);
}

export function formatDuration(ms: number | undefined): string {
  if (!ms) return '';
  const min = Math.round(ms / 60000);
  const h = Math.floor(min / 60);
  return h ? `${h}u ${min % 60}m` : `${min} min`;
}

export function formatClock(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  const mm = String(m).padStart(h ? 2 : 1, '0');
  return `${h ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

export function isWatched(item: PlexItem): boolean {
  if (item.type === 'show' || item.type === 'season') return !!item.leafCount && item.viewedLeafCount === item.leafCount;
  return (item.viewCount ?? 0) > 0;
}

/** TMDB-id uit de Plex-GUID's (alleen films en series; afleveringen hebben een eigen id). */
export function tmdbRef(item: PlexItem): { type: 'movie' | 'tv'; tmdbId: number } | null {
  if (item.type !== 'movie' && item.type !== 'show') return null;
  const g = item.Guid?.find((x) => x.id.startsWith('tmdb://'));
  const id = g ? Number(g.id.slice(7)) : NaN;
  return Number.isFinite(id) && id > 0 ? { type: item.type === 'show' ? 'tv' : 'movie', tmdbId: id } : null;
}
